import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CheckCircle2, MessageSquareText, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/lib/auth";
import api, { formatApiError } from "@/lib/api";

const REASONS = ["new_game", "edit_game", "technical", "billing", "other"];
const GAME_TYPES = [
  "spin_wheel", "scratch_card", "card_game", "slot_machine", "lottery_draw", "prediction_match", "guess_price",
  "quiz", "ninja", "blow_candles", "flappy", "mystery_box", "registration_gift", "advent_calendar",
];
const NONE = "none";

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-medium">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function PickList({ value, onChange, options, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="rounded-xl h-10" data-testid={testId}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="max-h-72">
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value} data-testid={`${testId}-${o.value}`}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// Reason + game picker: game types for a new game, own games for edit / technical issue.
function ReasonFields({ form, set, games }) {
  const { t } = useTranslation();
  const reasonOpts = REASONS.map((r) => ({ value: r, label: t(`contact_admin.reasons.${r}`) }));
  const typeOpts = [
    ...GAME_TYPES.map((g) => ({ value: g, label: t(`contact_admin.types.${g}`) })),
    { value: NONE, label: t("contact_admin.game_type_undecided") },
  ];
  const gameOpts = [{ value: NONE, label: t("contact_admin.game_none") }, ...games.map((g) => ({ value: g.id, label: g.title }))];
  const pickGame = form.reason === "edit_game" || form.reason === "technical";
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <Field label={t("contact_admin.reason")}>
        <PickList value={form.reason} onChange={(v) => set({ reason: v })} options={reasonOpts} testId="contact-admin-reason" />
      </Field>
      {form.reason === "new_game" && (
        <Field label={t("contact_admin.game_type")}>
          <PickList value={form.game_type} onChange={(v) => set({ game_type: v })} options={typeOpts} testId="contact-admin-game-type" />
        </Field>
      )}
      {pickGame && (
        <Field label={t("contact_admin.game_concerned")}>
          <PickList value={form.game_id} onChange={(v) => set({ game_id: v })} options={gameOpts} testId="contact-admin-game" />
        </Field>
      )}
    </div>
  );
}

function IdentityFields({ form, set }) {
  const { t } = useTranslation();
  const input = (key, type = "text", ph = "") => (
    <Input type={type} value={form[key]} placeholder={ph} onChange={(e) => set({ [key]: e.target.value })}
      className="rounded-xl h-10" data-testid={`contact-admin-${key}-input`} />
  );
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <Field label={`${t("contact_admin.name")} *`}>{input("name")}</Field>
      <Field label={`${t("contact_admin.email")} *`}>{input("email", "email")}</Field>
      <Field label={t("contact_admin.company")}>{input("company")}</Field>
      <Field label={t("contact_admin.phone")}>{input("phone", "tel", t("contact_admin.optional"))}</Field>
    </div>
  );
}

function SuccessView({ name, email, onClose }) {
  const { t } = useTranslation();
  return (
    <div className="py-6 text-center" data-testid="contact-admin-success">
      <div className="mx-auto h-12 w-12 rounded-2xl bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
        <CheckCircle2 className="h-6 w-6" />
      </div>
      <h3 className="mt-4 text-xl font-semibold tracking-tight">{t("contact_admin.success_title")}</h3>
      <p className="mt-2 text-sm text-muted-foreground">{t("contact_admin.success_body", { name, email })}</p>
      <Button onClick={onClose} className="mt-6 rounded-full" data-testid="contact-admin-close-btn">
        {t("contact_admin.close")}
      </Button>
    </div>
  );
}

function useContactData(open, user) {
  const [form, setForm] = useState(null);
  const [games, setGames] = useState([]);
  useEffect(() => {
    if (!open) return;
    setForm({ name: user?.name || "", email: user?.email || "", company: "", phone: "", reason: "new_game", game_type: "spin_wheel", game_id: NONE, message: "" });
    api.get("/merchant/profile").then((r) => {
      const m = r.data || {};
      setForm((f) => f && { ...f, email: m.contact_email || f.email, company: m.legal_name || m.company || m.name || "" });
    }).catch(() => {});
    api.get("/games").then((r) => setGames(r.data?.games || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  return { form, setForm, games };
}

export function ContactAdminDialog({ open, onOpenChange }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { form, setForm, games } = useContactData(open, user);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => { if (open) setSent(false); }, [open]);

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return toast.error(t("contact_admin.required"));
    setSending(true);
    try {
      await api.post("/merchant/contact", {
        ...form,
        game_type: form.reason === "new_game" && form.game_type !== NONE ? form.game_type : null,
        game_id: form.game_id !== NONE ? form.game_id : null,
      });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl rounded-2xl max-h-[92vh] overflow-y-auto" data-testid="contact-admin-dialog">
        {sent ? (
          <SuccessView name={form.name} email={form.email} onClose={() => onOpenChange(false)} />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span className="h-8 w-8 rounded-xl qrise-gradient flex items-center justify-center">
                  <MessageSquareText className="h-4 w-4 text-white" />
                </span>
                {t("contact_admin.title")}
              </DialogTitle>
              <DialogDescription>{t("contact_admin.subtitle")}</DialogDescription>
            </DialogHeader>
            {form && (
              <form onSubmit={submit} className="space-y-4" data-testid="contact-admin-form">
                <ReasonFields form={form} set={set} games={games} />
                <IdentityFields form={form} set={set} />
                <Field label={`${t("contact_admin.message")} *`}>
                  <Textarea rows={5} value={form.message} placeholder={t("contact_admin.message_ph")}
                    onChange={(e) => set({ message: e.target.value })} className="rounded-xl resize-y" data-testid="contact-admin-message-input" />
                </Field>
                <div className="flex justify-end gap-2 pt-1">
                  <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="contact-admin-cancel-btn">
                    {t("contact_admin.cancel")}
                  </Button>
                  <Button type="submit" disabled={sending} className="rounded-full gap-1.5 bg-foreground text-background hover:bg-foreground/90" data-testid="contact-admin-submit-btn">
                    <Send className="h-3.5 w-3.5" />
                    {sending ? t("contact_admin.sending") : t("contact_admin.send")}
                  </Button>
                </div>
              </form>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function ContactAdminButton() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}
        className="rounded-full gap-1.5 mr-1 transition-colors hover:border-purple-400/60" data-testid="contact-admin-btn">
        <MessageSquareText className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">{t("contact_admin.cta")}</span>
      </Button>
      <ContactAdminDialog open={open} onOpenChange={setOpen} />
    </>
  );
}

export default ContactAdminButton;
